import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function useLinkedInPosts() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchLinkedInPosts() {
      try {
        setLoading(true)
        setError(null)

        const { data, error: fetchError } = await supabase
          .from('linkedin_posts')
          .select('*')
          .eq('published', true)

        if (fetchError) {
          throw fetchError
        }

        // Transform the data to match the existing structure
        const transformedPosts = data.map(item => ({
          title: item.title,
          date: item.date,
          content: item.content || '',
          image: item.image ? (item.image.startsWith('http') ? item.image : process.env.PUBLIC_URL + item.image) : '',
          link: item.link, // External LinkedIn permalink
          author: item.author || '',
          linkedinId: item.linkedin_id,
          orderPriority: item.order_priority,
          id: item.id,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }))

        // Helper function to parse date strings like "August 4, 2026" or "Aug 4th 2026"
        const parsePostDate = (dateStr) => {
          try {
            // Remove ordinal suffixes (st, nd, rd, th)
            const cleaned = dateStr.replace(/(\d+)(st|nd|rd|th)/g, '$1')
            const parsed = new Date(cleaned)
            // If valid date, return it; otherwise return a very old date
            return isNaN(parsed.getTime()) ? new Date(0) : parsed
          } catch {
            return new Date(0) // Return epoch if parsing fails
          }
        }

        // Sort function: order_priority first (nulls last), then by date (newest first)
        const sortPosts = (a, b) => {
          if (a.orderPriority != null && b.orderPriority != null) {
            return a.orderPriority - b.orderPriority
          }
          if (a.orderPriority != null) return -1
          if (b.orderPriority != null) return 1
          const dateA = parsePostDate(a.date)
          const dateB = parsePostDate(b.date)
          return dateB - dateA // Descending order (newest first)
        }

        setPosts(transformedPosts.sort(sortPosts))
      } catch (err) {
        console.error('Error fetching LinkedIn posts:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchLinkedInPosts()
  }, [])

  return { posts, loading, error }
}
